import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { SignIn, SignUp, useUser, useAuth } from '@clerk/clerk-react';
import { lazy, Suspense, useEffect, useState } from 'react';
import Layout from './components/Layout';
import WelcomeFlow from './components/WelcomeFlow';
import { settings, customers, setTokenGetter } from './lib/api';
import { SiteProvider } from './contexts/SiteContext';
import { AIProvider } from './contexts/AIContext';

// Lazy load pages for code-splitting
const WizardPage = lazy(() => import('./pages/WizardPage'));
const PagesPage = lazy(() => import('./pages/PagesPage'));
const EditorPage = lazy(() => import('./pages/EditorPage'));
const EditorPageNew = lazy(() => import('./pages/EditorPageNew').then(m => ({ default: m.EditorPageNew })));
const RevisionsPage = lazy(() => import('./pages/RevisionsPage'));
const MediaPage = lazy(() => import('./pages/MediaPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const DesignPage = lazy(() => import('./pages/DesignPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const SitesPage = lazy(() => import('./pages/SitesPage'));
const DomainsPage = lazy(() => import('./pages/DomainsPage'));
const OverviewPage = lazy(() => import('./pages/OverviewPage'));
const SEOPage = lazy(() => import('./pages/SEOPage'));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage'));

// Loading component for lazy-loaded routes
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

function App() {
  const { user, isSignedIn, isLoaded } = useUser();
  const { getToken } = useAuth();
  const [customerInitialized, setCustomerInitialized] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(
    localStorage.getItem('onboarding_completed') === 'true'
  );

  // For development without Clerk
  const isDev = !import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

  // Set token getter for API client
  useEffect(() => {
    if (!isDev && getToken) {
      setTokenGetter(getToken);
    }
  }, [isDev, getToken]);

  useEffect(() => {
    // Initialize customer ID if not set (for dev)
    if (isDev && !settings.getCustomerId()) {
      settings.setCustomerId('dev-customer');
      settings.setSubdomain('demo');
      setCustomerInitialized(true);
    }
  }, [isDev]);

  // Initialize customer when Clerk user is loaded
  useEffect(() => {
    const initializeCustomer = async () => {
      if (!isSignedIn || !user || isDev || customerInitialized) return;

      try {
        // Check if customer already exists (JWT token identifies the user)
        const existing = await customers.getMe();
        if (existing.customer) {
          settings.setCustomerId(existing.customer.id);
          settings.setSubdomain(existing.customer.subdomain);
          setCustomerInitialized(true);
          setIsNewUser(false);
          return;
        }
      } catch (error) {
        // Customer doesn't exist, create one
        console.log('Creating new customer...');
        try {
          const result = await customers.create({
            businessName: user.fullName || user.primaryEmailAddress?.emailAddress?.split('@')[0] || 'My Business',
            email: user.primaryEmailAddress?.emailAddress || '',
            clerkUserId: user.id,
          });

          if (result.customer) {
            settings.setCustomerId(result.customer.id);
            settings.setSubdomain(result.customer.subdomain);
            setCustomerInitialized(true);
            setIsNewUser(true); // Mark as new user
          }
        } catch (createError) {
          console.error('Failed to create customer:', createError);
        }
      }
    };

    initializeCustomer();
  }, [isSignedIn, user, isDev, customerInitialized]);

  // Set auth cookie for automatic edit mode on live sites
  useEffect(() => {
    if (isSignedIn && customerInitialized) {
      const customerId = settings.getCustomerId();
      if (customerId) {
        // Set cookie on .imerkar.com domain so it's accessible on all subdomains
        const domain = import.meta.env.MODE === 'production' ? '.imerkar.com' : 'localhost';
        document.cookie = `imerkar_auth=${customerId}; path=/; domain=${domain}; max-age=604800; SameSite=Lax`;
      }
    } else {
      // Clear cookie when logged out
      const domain = import.meta.env.MODE === 'production' ? '.imerkar.com' : 'localhost';
      document.cookie = `imerkar_auth=; path=/; domain=${domain}; max-age=0`;
    }
  }, [isSignedIn, customerInitialized]);

  // Show loading while Clerk initializes
  if (!isLoaded && !isDev) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Require sign-in (unless in dev mode)
  if (!isSignedIn && !isDev) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Website Builder</h1>
            <p className="text-gray-600">Sign in to manage your site</p>
          </div>
          <SignIn routing="hash" signUpUrl="/#/sign-up" />
        </div>
      </div>
    );
  }

  // Show welcome flow for new users who haven't completed onboarding
  if (isSignedIn && customerInitialized && isNewUser && !onboardingCompleted) {
    return (
      <WelcomeFlow
        onComplete={() => {
          localStorage.setItem('onboarding_completed', 'true');
          setOnboardingCompleted(true);
        }}
      />
    );
  }

  return (
    <BrowserRouter>
      <SiteProvider>
        <AIProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Layout />}>
                <Route index element={<Navigate to="/overview" replace />} />
                <Route path="wizard" element={<WizardPage />} />
                <Route path="sites" element={<SitesPage />} />
                <Route path="overview" element={<OverviewPage />} />
                <Route path="domains" element={<DomainsPage />} />
                <Route path="pages" element={<PagesPage />} />
                <Route path="pages/:pageId/edit" element={<EditorPage />} />
                <Route path="editor/:pageId" element={<EditorPageNew />} />
                <Route path="media" element={<MediaPage />} />
                <Route path="contact" element={<ContactPage />} />
                <Route path="design" element={<DesignPage />} />
                <Route path="revisions" element={<RevisionsPage />} />
                <Route path="seo" element={<SEOPage />} />
                <Route path="templates" element={<TemplatesPage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>
              <Route path="/sign-up" element={
                <div className="min-h-screen bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center p-4">
                  <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
                    <SignUp routing="hash" signInUrl="/#/sign-in" />
                  </div>
                </div>
              } />
            </Routes>
          </Suspense>
        </AIProvider>
      </SiteProvider>
    </BrowserRouter>
  );
}

export default App;
